"use client";

import React, { useEffect, useMemo } from "react";

import { useGetSubscriptionPlanQuery } from "@/app/redux/features/api/common";
import { SubscriptionCustomFeature, SubscriptionFeatures } from "@/app/(general-header)/subscription/interfaces";
import CloseModalIcon from "./icons/CloseModalIcon";

interface BenefitsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const tryAndGetValue = (key: string, metaData: [string, string][]): string => {
  const item = metaData.find(item => item[0] === key);
  return item ? item[1] : "0";
};

const getFeatureEntries = (feature: string): number => {
  const match = feature.match(/([0-9]+)\s(tickets|entries)/);
  return match?.[1] ? Number(match[1]) : -1;
};

const BenefitsModal = ({ isOpen, onClose }: BenefitsModalProps) => {
  const { data: subscription } = useGetSubscriptionPlanQuery(undefined, { skip: !isOpen });

  useEffect(() => {
    if (!isOpen) return;
    document.querySelector("body")?.classList.add("overflow-hidden");
    return () => {
      document.querySelector("body")?.classList.remove("overflow-hidden");
    };
  }, [isOpen]);

  const totalEntries = useMemo(() => {
    if (!subscription?.metadata) return "0";
    return tryAndGetValue("total_entries", subscription.metadata);
  }, [subscription?.metadata]);

  const description = useMemo(() => {
    if (!subscription?.description) return null;
    const totalPrice = /£.*M/.exec(subscription.description);
    if (!totalPrice?.length) return null;
    return (
      <>
        Your membership gives you{" "}
        <span className="font-medium">{totalEntries} entries across 21 monthly competitions,</span> with prizes worth
        over <span className="font-medium">{totalPrice[0]}.</span> <br />
        From dream homes and supercars to life changing cash, here&#39;s what you can win:
      </>
    );
  }, [subscription?.description, totalEntries]);

  const features = useMemo<SubscriptionCustomFeature[]>(() => {
    if (!subscription) return [];

    const metadata = subscription.metadata ?? [];
    const featureStrings = subscription.features ?? [];

    const hcTickets = tryAndGetValue("HC", metadata);
    const xcTickets = tryAndGetValue("XC", metadata);
    const lsTickets = tryAndGetValue("LS", metadata);
    const dcTickets = tryAndGetValue("DC", metadata);
    const iwTickets = tryAndGetValue("IW", metadata);
    const scTickets = tryAndGetValue("SC", metadata);

    const houseFeature: SubscriptionCustomFeature = {
      type: SubscriptionFeatures.DreamHouse,
      entries: 0,
      image: "/assets/images/subscriptions/house.webp",
      multiplier: 1,
      text: "",
    };
    const supercarFeature: SubscriptionCustomFeature = {
      type: SubscriptionFeatures.LuxuryCars,
      image: "/assets/images/subscriptions/xc-supercars.webp",
      entries: 0,
      multiplier: 6,
      text: "",
    };
    const lifestyleFeature: SubscriptionCustomFeature = {
      type: SubscriptionFeatures.LifestylePrizes,
      image: "/assets/images/subscriptions/lifestyle.webp",
      entries: 0,
      multiplier: 4,
      text: "",
    };
    const dreamCarFeature: SubscriptionCustomFeature = {
      type: SubscriptionFeatures.DreamCars,
      image: "/assets/images/subscriptions/stb-cars.webp",
      entries: 0,
      multiplier: 4,
      text: "",
    };
    const instantWinFeature: SubscriptionCustomFeature = {
      type: SubscriptionFeatures.InstantWins,
      image: "/assets/images/subscriptions/any-instant-wins.webp",
      entries: 0,
      multiplier: -1,
      text: "",
    };

    for (const feature of featureStrings) {
      if (feature.includes("Dream Home")) {
        houseFeature.text = (
          <>
            With the Pass, you&#39;ll have{" "}
            <strong>
              {hcTickets} {hcTickets == "1" ? "entry" : "entries"}
            </strong>{" "}
            in the monthly Dream Home competition, giving you the chance to win the prize of a lifetime.
          </>
        );
        houseFeature.entries = getFeatureEntries(feature);
      } else if (feature.includes("Luxury Cars") || feature.includes("Supercards")) {
        supercarFeature.text = (
          <>
            Get behind the wheel of a luxury or sports car! You&#39;ll receive{" "}
            <strong>
              {xcTickets} {xcTickets == "1" ? "guaranteed ticket" : "guaranteed tickets"}
            </strong>{" "}
            in any Luxury Car competition each month. With at least 6 distinct competitions monthly, your chances to win
            are stronger than ever.
          </>
        );
        supercarFeature.entries = getFeatureEntries(feature);
      } else if (feature.includes("Lifestyle")) {
        lifestyleFeature.text = (
          <>
            This is the competition where you choose what you want to win: cars, cash, tech, experiences, and more. With
            your subscription, you get{" "}
            <strong>
              {lsTickets} {lsTickets == "1" ? "ticket" : "tickets"}
            </strong>{" "}
            for each of the 4 Lifestyle competitions in a month.
          </>
        );
        lifestyleFeature.entries = getFeatureEntries(feature);
      } else if (feature.includes("Dream Car")) {
        dreamCarFeature.text = (
          <>
            Enjoy{" "}
            <strong>
              {dcTickets} {dcTickets == "1" ? "weekly ticket" : "weekly tickets"}
            </strong>{" "}
            for the iconic Spot The Ball game of skill. Compete every week and choose your prize from an extensive
            selection of brand-new cars!
          </>
        );
        dreamCarFeature.entries = getFeatureEntries(feature);
      } else if (feature.includes("Instant Win")) {
        instantWinFeature.text = (
          <>
            With{" "}
            <strong>
              {iwTickets} {iwTickets == "1" ? "ticket" : "tickets"} included
            </strong>
            , play in any of our Instant Wins competitions each month! Instant Wins is all about luck, so choose
            carefully where to use your entries.
          </>
        );
        instantWinFeature.entries = getFeatureEntries(feature);
      }
    }

    const passExclusiveFeature: SubscriptionCustomFeature = {
      type: SubscriptionFeatures.PassExclusiveComps,
      image: "/assets/images/subscriptions/pass-exclusive-comps.webp",
      entries: Number(scTickets) || 0,
      multiplier: "Daily",
      text: (
        <>
          A thank you to <strong>BOTB Pass members</strong>: automatic entry into exclusive daily competitions, with
          1,250 extra prizes every day &mdash; including cash, credit and tech.
        </>
      ),
    };

    return [houseFeature, supercarFeature, dreamCarFeature, lifestyleFeature, instantWinFeature, passExclusiveFeature];
  }, [subscription]);

  const formatMultiplier = (multiplier: number | string) => {
    if (typeof multiplier === "string") return multiplier;
    if (multiplier === -1) return "";
    return `${multiplier}x`;
  };

  const formatEntriesBadge = (feature: SubscriptionCustomFeature) => {
    if (feature.type === SubscriptionFeatures.PassExclusiveComps) {
      return `${feature.entries} entries/day`;
    }
    return `${feature.entries} entries`;
  };

  if (!isOpen) return null;

  return (
    <div>
      <div data-name="Overlay" className="fixed inset-0 z-[9999] bg-black/80" />
      <div
        data-name="PopupContainer"
        className={"fixed inset-0 z-[99999] w-full min-h-full pt-[30px] overflow-x-hidden overflow-y-auto"}>
        <div className={"w-full min-h-full flex items-center justify-center flex-col m-auto"}>
          <div
            className={
              "bg-white relative w-full lg:max-w-[1000px] lg:min-h-[614px] px-4 pt-10 pb-4 lg:px-14 lg:py-6 flex flex-col max-lg:grow rounded-t-[20px] lg:rounded-2xl"
            }>
            <button
              onClick={onClose}
              className={"absolute flex items-center justify-center right-3 top-3 lg:right-4 lg:top-4 lg:size-10"}>
              <CloseModalIcon className={"size-6 text-gray-5"} />
            </button>

            <div data-name={"modal-step-1"} className={"h-full grow flex flex-col"}>
              <div className={"scroll-wrapper grow relative"}>
                <div className={"max-lg:absolute max-lg:inset-0 size-full max-lg:overflow-y-auto"}>
                  <div
                    className={
                      "text-secondary text-center font-jost text-lg font-medium leading-6 mb-6 lg:mb-8 lg:text-2xl lg:leading-7.5"
                    }>
                    {`You're a member of `}
                    <br className={"lg:hidden"} /> BOTB Pass - {subscription?.name} Plan
                  </div>

                  <div className={"flex items-center gap-3 mb-4 lg:mb-5"}>
                    <img
                      src={"/assets/images/subscriptions/membership-total.webp"}
                      alt={"membership-total"}
                      width={64}
                      height={64}
                      className={"max-lg:hidden size-6 lg:size-16 aspect-square shrink-0"}
                    />
                    <div className={"flex lg:max-w-[795px] flex-col justify-center items-start gap-2 lg:gap-3"}>
                      <div className={"max-lg:flex max-lg:gap-1.5 max-lg:items-center"}>
                        <img
                          src={"/assets/images/subscriptions/membership-total.webp"}
                          alt={"membership-total"}
                          width={64}
                          height={64}
                          className={"lg:hidden size-6 lg:size-16 aspect-square shrink-0"}
                        />
                        <div
                          className={
                            "text-sm font-medium leading-5 text-secondary font-jost lg:text-xl lg:leading-6.5"
                          }>
                          <span className={"text-primary font-semibold"}>{totalEntries}</span> Total Entries for 21
                          competitions
                        </div>
                      </div>
                      <div
                        className={"text-xs font-normal leading-4 text-gray-1 font-jost lg:text-base lg:leading-5.5"}>
                        {description}
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className={"grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-x-2.5 lg:gap-y-4"}>
                      {features.map(feature => (
                        <div
                          key={feature.type}
                          className={
                            "lg:min-h-[142px] flex items-start gap-2 lg:gap-3 w-full border border-black/8 rounded-lg px-2 relative pt-7 pb-5"
                          }>
                          <span
                            className={
                              "absolute right-0 top-0 flex justify-center items-center gap-2 bg-orange-1 px-3 py-1 rounded-bl-lg rounded-tr-lg text-dark-1 font-jost text-xs font-normal leading-4 lg:text-sm lg:leading-5"
                            }>
                            {formatEntriesBadge(feature)}
                          </span>
                          <img
                            src={feature.image}
                            alt={feature.type}
                            width={72}
                            height={72}
                            className={"size-14 lg:size-[72px] aspect-square shrink-0"}
                          />
                          <div className={"flex flex-col items-start gap-1"}>
                            <div
                              className={
                                "text-gray-1 font-jost text-sm font-medium leading-5 lg:text-base lg:leading-6.5"
                              }>
                              <span className={"text-primary text-base lg:text-xl font-bold"}>
                                {formatMultiplier(feature.multiplier)}
                              </span>{" "}
                              {feature.type}
                            </div>
                            <div
                              className={
                                "text-gray-1 font-jost text-[11px] font-normal leading-3.5 lg:text-[13px] lg:leading-4.5"
                              }>
                              {feature.text}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BenefitsModal;
